import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { sendMessage } from "../../../services/messageServices";
import { getUserById } from "../../../services/userServices";
import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";

import { IoIosSend } from "react-icons/io";
import { FaSpinner, FaTimes } from "react-icons/fa";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

function roleMeta(roleId) {
  if (Number(roleId) === 1) return { label: "Administrateur", tone: "red" };
  if (Number(roleId) === 3) return { label: "Enseignant", tone: "green" };
  return { label: "Utilisateur", tone: "slate" };
}

function roleBadgeClass(tone) {
  switch (tone) {
    case "red":
      return "bg-red-600 text-white";
    case "green":
      return "bg-emerald-600 text-white";
    default:
      return "bg-slate-600 text-white";
  }
}

export default function CreateResponseMessageForm({
  onSuccess,
  show,
  onHide,
  userId,
  initialRecipientId,
  initialSubject,
}) {
  const [messageData, setMessageData] = useState({
    recipient_id: initialRecipientId || "",
    subject: initialSubject || "",
    content: "",
  });

  const [recipient, setRecipient] = useState(null);
  const [loadingRecipient, setLoadingRecipient] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setMessageData({
      recipient_id: initialRecipientId || "",
      subject: initialSubject || "",
      content: "",
    });
  }, [initialRecipientId, initialSubject, show]);

  useEffect(() => {
    if (!show || !initialRecipientId) return;

    let cancelled = false;

    async function fetchRecipient() {
      try {
        setLoadingRecipient(true);

        const user = await getUserById(initialRecipientId);

        if (!cancelled) {
          setRecipient(user || null);
        }
      } catch (error) {
        console.error("Failed to fetch user details:", error);

        if (!cancelled) {
          setRecipient(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingRecipient(false);
        }
      }
    }

    fetchRecipient();

    return () => {
      cancelled = true;
    };
  }, [initialRecipientId, show]);

  const fullName = useMemo(() => {
    if (!recipient) return "";
    return `${recipient.nom || ""} ${recipient.prenom || ""}`.trim();
  }, [recipient]);

  const avatar = toAbsoluteUserPhotoUrl(
    recipient?.photoURL || recipient?.photo_url || "/img/user_avatar.jpg"
  );

  const { label: roleLabel, tone: roleTone } = roleMeta(recipient?.role_id);

  const canSubmit =
    Boolean(messageData.recipient_id) &&
    Boolean(messageData.subject) &&
    Boolean(messageData.content) &&
    messageData.content !== "<p><br></p>";

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmit) {
      toast.error("Merci de remplir tous les champs.");
      return;
    }

    try {
      setSending(true);

      await sendMessage({
        sender_id: userId,
        recipient_id: messageData.recipient_id,
        subject: String(messageData.subject || "").trim(),
        content: messageData.content,
      });

      toast.success("Message envoyé avec succès.");
      onSuccess?.();
      onHide?.();
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Erreur lors de l'envoi du message.");
    } finally {
      setSending(false);
    }
  };

  if (!show) return null;

  return (
    <section className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-white shadow-lg shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950 dark:shadow-black/10">
      <div className="bg-slate-950 px-4 py-4 text-white">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            {loadingRecipient ? (
              <div className="h-10 w-10 animate-pulse rounded-full bg-white/20" />
            ) : (
              <img
                src={avatar}
                alt={fullName}
                className="h-10 w-10 rounded-full object-cover ring-1 ring-white/30"
              />
            )}

            <div className="min-w-0">
              <p className="truncate text-sm font-bold">
                Réponse à {loadingRecipient ? "..." : fullName || "Utilisateur"}
              </p>

              {!loadingRecipient && (
                <span
                  className={`mt-1 inline-flex items-center rounded-full px-2 py-[2px] text-[10px] font-bold ${roleBadgeClass(
                    roleTone
                  )}`}
                >
                  {roleLabel}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onHide}
            disabled={sending}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/10 transition hover:bg-white/20 disabled:opacity-60"
            aria-label="Fermer"
          >
            <FaTimes />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 p-4">
        <div>
          <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
            Sujet
          </label>

          <input
            type="text"
            value={messageData.subject}
            onChange={(event) =>
              setMessageData((previous) => ({
                ...previous,
                subject: event.target.value,
              }))
            }
            className="min-h-12 w-full rounded-2xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900 outline-none transition focus:ring-2 focus:ring-violet-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            required
          />
        </div>

        <div>
          <label className="mb-1.5 block text-sm font-bold text-slate-900 dark:text-slate-100">
            Contenu
          </label>

          <div className="overflow-hidden rounded-2xl border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-900 [&_.ql-editor]:min-h-[160px] [&_.ql-toolbar]:border-0 [&_.ql-container]:border-0">
            <ReactQuill
              theme="snow"
              value={messageData.content}
              onChange={(content) =>
                setMessageData((previous) => ({
                  ...previous,
                  content,
                }))
              }
              modules={quillModules}
              formats={quillFormats}
              placeholder="Rédigez votre réponse…"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onHide}
            disabled={sending}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={sending || loadingRecipient || !canSubmit}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-violet-600 px-4 text-sm font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {sending ? <FaSpinner className="animate-spin" /> : <IoIosSend />}
            {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </form>
    </section>
  );
}